import invariant from 'invariant';
import conformsTo from 'lodash/conformsTo';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import isString from 'lodash/isString';
import { PRODUCTION } from '../config';
import checkStore from './checkStore';
import { DAEMON, ONCE_TILL_UNMOUNT, RESTART_ON_REMOUNT } from './constants';
import { shouldReport } from './error';
import { SentryReport } from './sentry';

const allowedModes = [RESTART_ON_REMOUNT, DAEMON, ONCE_TILL_UNMOUNT];

const checkKey = (key) =>
  invariant(isString(key) && !isEmpty(key), '(app/utils...) injectSaga: Expected `key` to be a non empty string');

const checkDescriptor = (descriptor) => {
  const shape = {
    saga: isFunction,
    mode: (mode) => isString(mode) && allowedModes.includes(mode),
  };
  invariant(conformsTo(descriptor, shape), '(app/utils...) injectSaga: Expected a valid saga descriptor');
};

const isEntry = (value) => value && typeof value === 'object';
const isRunning = (task) => task && (!(task.isRunning instanceof Function) || task.isRunning());

const startSaga = (store, key, descriptor, args, owners, ownerlessCount) => {
  const task = store.runSaga(descriptor.saga, args);
  store.injectedSagas[key] = { ...descriptor, ownerlessCount, owners, task };

  if (task && task.done && task.done.catch instanceof Function) {
    task.done.catch((error) => {
      if (shouldReport(error)) {
        SentryReport({ error, fromSaga: true, state: store.getState() });
      }
    });
  }

  return task;
};

export function injectSagaFactory(store, isValid) {
  return function injectSaga(key, descriptor = {}, args, { owner } = {}) {
    if (!isValid) checkStore(store);

    const next = { ...descriptor, mode: descriptor.mode || RESTART_ON_REMOUNT };
    const current = store.injectedSagas[key];
    const entry = isEntry(current) ? current : null;

    checkKey(key);
    checkDescriptor(next);

    const owners = entry ? entry.owners || new Set() : new Set();
    const ownerlessCount = (entry?.ownerlessCount || 0) + (owner === undefined ? 1 : 0);
    if (owner !== undefined) owners.add(owner);
    if (entry) Object.assign(entry, { ownerlessCount, owners });

    const hotReplacement = !PRODUCTION && entry && entry.saga !== next.saga;
    if (hotReplacement) entry.task.cancel();

    const shouldStart =
      !current ||
      hotReplacement ||
      (current === 'done' && next.mode === RESTART_ON_REMOUNT) ||
      (entry &&
        !isRunning(entry.task) &&
        (next.mode === RESTART_ON_REMOUNT || (next.mode === ONCE_TILL_UNMOUNT && entry.replayOwner === owner)));

    return shouldStart ? startSaga(store, key, next, args, owners, ownerlessCount) : entry?.task;
  };
}

export function ejectSagaFactory(store, isValid) {
  return function ejectSaga(key, { owner } = {}) {
    if (!isValid) checkStore(store);

    checkKey(key);

    const entry = store.injectedSagas[key];
    if (!isEntry(entry)) return;
    if (owner === undefined) {
      if (!entry.ownerlessCount) return;
      entry.ownerlessCount -= 1;
    } else if (!entry.owners?.delete(owner)) return;
    if (entry.ownerlessCount || entry.owners.size || entry.mode === DAEMON) return;

    const taskWasRunning = isRunning(entry.task);
    entry.task.cancel();
    entry.replayOwner = entry.mode === ONCE_TILL_UNMOUNT && taskWasRunning ? owner : undefined;

    if (PRODUCTION) store.injectedSagas[key] = 'done';
  };
}

export default function getInjectors(store) {
  checkStore(store);

  return {
    injectSaga: injectSagaFactory(store, true),
    ejectSaga: ejectSagaFactory(store, true),
  };
}
